import { call, put, select, takeLatest } from 'redux-saga/effects'
import {
  ClientProgramsMap,
  FetchClientProgramsPayload,
  ProgramsCorrespondingToTenant,
} from '../types'
import { PayloadAction } from '@reduxjs/toolkit'
import { SagaIterator } from 'redux-saga'
import {
  addToaster,
  fetchAllClientPrograms,
  hideLoader,
  showLoader,
  updateAllClientPrograms,
  updateAuthUserSelectedTenant,
  updateSelectedClientProgram,
} from '../slices'
import { getAllClientProgramsCorrespondingToUser } from '../apis'
import { v4 as uuid } from 'uuid'
import { DASHBOARD_CONSTANTS } from '../constants'


const transformToClientProgramsMap = (
  data: {
    id: number
    name_of_organization: string
    programs: ProgramsCorrespondingToTenant[]
  }[]
): ClientProgramsMap => {
  return data.reduce<ClientProgramsMap>((acc, tenant) => {
    acc[tenant.id.toString()] = {
      name_of_organization: tenant.name_of_organization,
      programs: tenant.programs,
    }
    return acc
  }, {})
}

function* fetchAllClientProgramsFunction(
  action: PayloadAction<FetchClientProgramsPayload>
): SagaIterator {
  try {
    yield put(showLoader())
    const response = yield call(
      getAllClientProgramsCorrespondingToUser,
      action?.payload?.userId
    )
    yield put(
      updateAllClientPrograms(transformToClientProgramsMap(response.data))
    )
    let selectedTenant = yield select(
      (state) => state.authUser.selectedClientTenant
    )
    const allClientPrograms = yield select(
      (state) => state.clientPrograms?.allClientPrograms
    )
    const prevSelectedClientProgram = yield select(
      (state) => state?.clientPrograms?.selectedClientPrograms
    )
    if (!allClientPrograms) return
    if (parseInt(selectedTenant?.id) === -1) {
      const newTenant = Object.keys(allClientPrograms)[0]
      yield put(updateAuthUserSelectedTenant(newTenant))
      selectedTenant = yield select(
        (state) => state.authUser.selectedClientTenant
      )
    }

    if(allClientPrograms[selectedTenant?.id])
    {
      yield put(
        updateSelectedClientProgram({
          tenant_id: selectedTenant?.id,
          program_id:
            allClientPrograms[selectedTenant?.id]?.programs?.[0]?.id,
          operational_program_name:
            allClientPrograms[selectedTenant?.id]?.programs?.[0]
              ?.operational_program_name,
          name_of_organization: selectedTenant?.name_of_organization,
          isOverseer:
            allClientPrograms[
              selectedTenant?.id
            ]?.programs?.[0]?.permission_set_name.includes('Overseeing'),
        })
      )
    }
    //if persisted prev selectedClientProgram is present and is present in allClientPrograms
    else if (allClientPrograms[prevSelectedClientProgram?.tenant_id]) {
      if (selectedTenant?.id !== prevSelectedClientProgram?.tenant_id) {
        yield put(
          updateAuthUserSelectedTenant(prevSelectedClientProgram?.tenant_id)
        )
        selectedTenant = yield select(
          (state) => state.authUser.selectedClientTenant
        )
      }
      yield put(
        updateSelectedClientProgram({
          tenant_id: selectedTenant?.id,
          program_id:
            prevSelectedClientProgram?.program_id ??
            allClientPrograms[selectedTenant?.id]?.programs?.[0]?.id,
          operational_program_name:
            prevSelectedClientProgram?.program_id ??
            allClientPrograms[selectedTenant?.id]?.programs?.[0]
              ?.operational_program_name,
          name_of_organization: selectedTenant?.name_of_organization,
          isOverseer:
            prevSelectedClientProgram?.isOverseer ??
            allClientPrograms[
              selectedTenant?.id
            ]?.programs?.[0]?.permission_set_name.includes('Overseeing'),
        })
      )
    } else {
      //if selectedTenant doesn't exists in allClientPrograms
      if (!allClientPrograms[selectedTenant?.id]) {
        const newTenant = Object.keys(allClientPrograms)[0]
        yield put(updateAuthUserSelectedTenant(newTenant))
        selectedTenant = yield select(
          (state) => state.authUser.selectedClientTenant
        )
      }
      yield put(addToaster({
        id: uuid(),
        message: DASHBOARD_CONSTANTS.NO_PROGRAM_CONFIGURED,
        type: 'error',
      }));
      
      yield put(
        updateSelectedClientProgram({
          tenant_id: selectedTenant?.id,
          program_id: allClientPrograms[selectedTenant?.id]?.programs?.[0]?.id,
          operational_program_name:
            allClientPrograms[selectedTenant?.id]?.programs?.[0]
              ?.operational_program_name,
          name_of_organization: selectedTenant?.name_of_organization,
          isOverseer:
            allClientPrograms[
              selectedTenant?.id
            ]?.programs?.[0]?.permission_set_name.includes('Overseeing'),
        })
      )
    }
  } catch (err) {
    console.log(err)
  } finally {
    yield put(hideLoader())
  }
}

export function* watchFetchAllClientPrograms() {
  yield takeLatest(fetchAllClientPrograms.type, fetchAllClientProgramsFunction)
}
